/**
 * Fase 5 — theme helper.
 * Single source of truth for the app's theme system (light / dark / system).
 * The <script> in `public/index.html` mirrors this logic to avoid a
 * dark→light flash on first paint.
 */
export const STORAGE_KEY = "rostr.theme";       // Fase 5 canonical key
export const LEGACY_KEY = "rooster.theme";      // pre-Fase 5 (light/dark only)
export const VALID = ["light", "dark", "system"];

export function readTheme() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (VALID.includes(v)) return v;
    // Migrate pre-Fase 5 users who only had light/dark stored — respect the
    // explicit choice they made once.
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === "light" || legacy === "dark") return legacy;
  } catch { /* localStorage disabled */ }
  return "system";
}

export function saveTheme(theme) {
  if (!VALID.includes(theme)) return;
  try {
    localStorage.setItem(STORAGE_KEY, theme);
    // Keep legacy key in sync so components still reading it don't lag.
    if (theme !== "system") localStorage.setItem(LEGACY_KEY, theme);
  } catch { /* ignore */ }
}

export function effectiveTheme(pref) {
  if (pref === "dark") return "dark";
  if (pref === "light") return "light";
  const m = window.matchMedia("(prefers-color-scheme: dark)");
  return m.matches ? "dark" : "light";
}

export function applyTheme(pref) {
  const eff = effectiveTheme(pref);
  document.documentElement.classList.toggle("dark", eff === "dark");
  document.documentElement.dataset.theme = eff;
}

// Bind a single media-query listener that only fires when the caller is in
// "system" mode. Returns an unsubscribe fn.
export function watchSystemTheme(cb) {
  const m = window.matchMedia("(prefers-color-scheme: dark)");
  const handler = () => cb(m.matches ? "dark" : "light");
  try { m.addEventListener("change", handler); } catch { m.addListener(handler); }
  return () => {
    try { m.removeEventListener("change", handler); } catch { m.removeListener(handler); }
  };
}
